import { useEffect, useRef, useState } from 'react';
import { Controller, useFormState, useWatch } from 'react-hook-form';
import type { Control, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { FieldError } from '@/shared/components/ui/field';
import { FormFieldSelect } from '@/shared/components/custom/form-field/FormFieldSelect';
import { FormFieldCheckbox } from '@/shared/components/custom/form-field/FormFieldCheckbox';
import { FloatingLabelDatePicker } from '@/shared/components/custom/form-field/FloatingLabelDatePicker';
import { useProjects } from '@/features/projects/public';
import { useBoards } from '@/features/boards/public';
import type { AddWidgetFormInput } from '../../schemas';
import type { DashboardScopeType } from '../../types';
import { FilterBuilder } from './FilterBuilder';

// completedAt is intentionally absent: WidgetDataService only resolves these three.
const DATE_FIELDS = ['createdAt', 'dueDate', 'startDate'] as const;
const DATE_PRESETS = ['this_week', 'this_month', 'last_7', 'last_30', 'custom'] as const;

interface WidgetDataFieldsProps {
  control: Control<AddWidgetFormInput>;
  setValue: UseFormSetValue<AddWidgetFormInput>;
  scopeType: DashboardScopeType;
  /** Owning project id when on a project or board dashboard. */
  dashboardProjectId?: string;
  /** Current board id when on a board dashboard — used to fetch boards from the right project. */
  dashboardBoardId?: string;
}

export function WidgetDataFields({
  control,
  setValue,
  scopeType,
  dashboardProjectId,
  dashboardBoardId,
}: WidgetDataFieldsProps) {
  const { t } = useTranslation('dashboard');
  const [open, setOpen] = useState(false);

  const watchedProjectId = useWatch({ control, name: 'scopeProjectId' });
  const watchedBoardId = useWatch({ control, name: 'scopeBoardId' });
  const preset = useWatch({ control, name: 'dateRangePreset' });

  // Everything below lives in a collapsed panel, so an error here would be
  // invisible and the submit button would look broken — open it instead.
  const { errors } = useFormState({ control });
  const hasError = !!errors.filters || !!errors.dateRangeStart || !!errors.dateRangePreset;
  const [prevHasError, setPrevHasError] = useState(hasError);
  if (hasError !== prevHasError) {
    setPrevHasError(hasError);
    if (hasError) setOpen(true);
  }

  // A board from a previous project would fail the backend's board/project
  // consistency check (Dashboard.BoardScopeMismatch).
  const prevProjectRef = useRef(watchedProjectId);
  useEffect(() => {
    if (prevProjectRef.current === watchedProjectId) return;
    prevProjectRef.current = watchedProjectId;
    setValue('scopeBoardId', undefined);
  }, [watchedProjectId, setValue]);

  // On project/board scope the project is implicit — use it directly instead of the form value.
  const effectiveProjectId =
    scopeType === 'project' || scopeType === 'board' ? dashboardProjectId : watchedProjectId;
  // On board scope the board is implicit too; otherwise use whatever board the
  // user pinned in "Alcance" — this resolves status/group filter options.
  const effectiveBoardId = scopeType === 'board' ? dashboardBoardId : watchedBoardId;

  const { data: projects } = useProjects();
  const { boards } = useBoards(effectiveProjectId ?? '');

  return (
    <div className="rounded-md border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium"
        data-testid="dashboard-advanced-toggle"
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {t('addWidget.form.advanced')}
        <span className="text-xs font-normal text-muted-foreground">
          {t('addWidget.form.advancedHint')}
        </span>
      </button>

      {open && (
        <div className="flex flex-col gap-5 border-t px-3 py-4">
          {/* Scope — omitted entirely on a board dashboard, where both the
              project and the board are already fixed by the route. */}
          {scopeType !== 'board' && (
            <div className="flex flex-col gap-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('addWidget.form.scope')}
              </p>
              {scopeType === 'tenant' && (
                <FormFieldSelect
                  control={control}
                  name="scopeProjectId"
                  label={t('addWidget.form.scopeProject')}
                  floatingLabel
                  options={(projects ?? []).flatMap((p) =>
                    p.id ? [{ value: p.id, label: p.name ?? '' }] : [],
                  )}
                  data-testid="dashboard-scope-project-select"
                />
              )}
              <FormFieldSelect
                control={control}
                name="scopeBoardId"
                label={t('addWidget.form.scopeBoard')}
                floatingLabel
                disabled={!effectiveProjectId}
                options={boards.flatMap((b) =>
                  b.id ? [{ value: b.id as string, label: b.name ?? '' }] : [],
                )}
                data-testid="dashboard-scope-board-select"
              />
            </div>
          )}

          {/* Archived tasks — the backend treats this as an exclusive switch:
              either only archived tasks or only active ones, never both. */}
          <FormFieldCheckbox
            control={control}
            name="archivedOnly"
            label={t('addWidget.form.archivedOnly')}
            data-testid="dashboard-archived-only-checkbox"
          />

          {/* Date range */}
          <div className="flex flex-col gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('addWidget.form.dateRange')}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <FormFieldSelect
                control={control}
                name="dateRangeField"
                label={t('addWidget.form.dateRangeField')}
                floatingLabel
                options={DATE_FIELDS.map((f) => ({
                  value: f,
                  label: t(`addWidget.form.dateRangeFields.${f}`),
                }))}
                data-testid="dashboard-daterange-field-select"
              />
              <FormFieldSelect
                control={control}
                name="dateRangePreset"
                label={t('addWidget.form.dateRangePreset')}
                floatingLabel
                options={DATE_PRESETS.map((p) => ({
                  value: p,
                  label: t(`addWidget.form.dateRangeOptions.${p}`),
                }))}
                data-testid="dashboard-daterange-preset-select"
              />
            </div>
            {preset === 'custom' && (
              <div className="grid grid-cols-2 gap-3">
                <Controller
                  control={control}
                  name="dateRangeStart"
                  render={({ field }) => (
                    <FloatingLabelDatePicker
                      id="dateRangeStart"
                      label={t('addWidget.form.dateRangeStart')}
                      value={field.value ?? null}
                      onChange={(value) => field.onChange(value ?? undefined)}
                      data-testid="dashboard-daterange-start"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="dateRangeEnd"
                  render={({ field }) => (
                    <FloatingLabelDatePicker
                      id="dateRangeEnd"
                      label={t('addWidget.form.dateRangeEnd')}
                      value={field.value ?? null}
                      onChange={(value) => field.onChange(value ?? undefined)}
                      data-testid="dashboard-daterange-end"
                    />
                  )}
                />
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-col gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('addWidget.form.filters')}
            </p>
            <Controller
              control={control}
              name="filters"
              render={({ field, fieldState }) => (
                <>
                  <FilterBuilder
                    value={field.value}
                    onChange={field.onChange}
                    projectId={effectiveProjectId}
                    boardId={effectiveBoardId}
                  />
                  {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                </>
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
}
